import styled from "@emotion/styled";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { Button, TextField } from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsEnvelope, BsTelegram, BsWhatsapp } from "react-icons/bs";
import {
  EmailShareButton,
  TelegramShareButton,
  WhatsappShareButton
} from "react-share";
import { useCopyToClipboard } from "react-use";
import { useDenominationStore } from "../store";

const Subtitle = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-weight: 700;
`;

const Social = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const SocialIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50px;
  background-color: #f8f8f8;
  display: grid;
  place-items: center;
  border: 1px solid ${({ selected }) => (selected ? "#00B0B9" : "#f8f8f8")};
`;

const SocialName = styled.span`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #8e8e8e;
`;

const CopyContainer = styled.div`
  display: flex;
  align-items: center;
  padding-block: 9px;
  padding-inline: 13px 6px;
  border: 1px solid #b3b3b3;
  border-radius: 5px;
`;

const URLText = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  flex: 1;
  word-break: break-word;
`;

export const GenerateReportFormModal = ({ open, onClose }) => {
  const [view, setView] = useState("default");

  const { t } = useTranslation();

  const views = {
    default: (
      <Stack alignItems="center" p={6}>
        <Stack spacing={2} sx={{ width: "210px" }}>
          {/* <Button>{t("denominations.reports.printReportForm")}</Button> */}
          <Button onClick={() => setView("link")}>
            {t("denominations.reports.generateReportFormLink")}
          </Button>
        </Stack>
      </Stack>
    ),
    print: t("denominations.reports.printReportForm"),
    link: <GeneratedFormView />
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="subtitle2">
          {t("denominations.reports.reportForm")}
        </Typography>
      </DialogTitle>
      <DialogContent>{views[view]}</DialogContent>
    </Dialog>
  );
};

const GeneratedFormView = () => {
  const [selectedSocial, setSelectedSocial] = useState("");
  const [copiedLink, copyLink] = useCopyToClipboard();
  const [reportTitle, setReportTitle] = useState("");
  const [reportStartDate, setReportStartDate] = useState(moment().toDate());
  const [reportEndDate, setReportEndDate] = useState(
    moment().add(3, "months").toDate()
  );
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const host = window.location.host;
  const protocol = window.location.protocol;

  const searchParams = new URLSearchParams();

  if (reportStartDate) {
    searchParams.set("start", moment(reportStartDate).format("YYYY-MM-DD"));
  }
  if (reportEndDate) {
    searchParams.set("end", moment(reportEndDate).format("YYYY-MM-DD"));
  }
  if (reportTitle) {
    searchParams.set("title", reportTitle);
  }

  const baseURL = `${protocol}//${host}/forms/denomination-reporting/${selectedDenomination?.id}/${selectedDenomination?.form_token}`;
  const queryString = searchParams.toString();
  const URL = queryString ? `${baseURL}?${queryString}` : baseURL;

  const { t } = useTranslation();

  return (
    <Stack spacing={3}>
      <TextField
        label={t("denominations.reports.reportTitle")}
        name="reportTitle"
        register={() => {}}
        value={reportTitle}
        onChange={e => setReportTitle(e.target.value)}
        hasNoBg
      />

      <DateField
        label={t("denominations.reports.reportStartDate")}
        hasNoBg
        value={reportStartDate}
        onChange={date => setReportStartDate(date)}
      />

      <DateField
        label={t("denominations.reports.reportEndDate")}
        hasNoBg
        value={reportEndDate}
        onChange={date => setReportEndDate(date)}
      />

      <Stack spacing={1}>
        <Subtitle>{t("members.applicationForm.ShareThis")}</Subtitle>
        <Stack direction="row" alignItems="center" spacing={4}>
          <Social onClick={() => setSelectedSocial("Email")}>
            <EmailShareButton url={URL}>
              <SocialIcon selected={selectedSocial === "Email"}>
                <BsEnvelope />
              </SocialIcon>
            </EmailShareButton>
            <SocialName>{t("members.applicationForm.Email")}</SocialName>
          </Social>
          <Social onClick={() => setSelectedSocial("Telegram")}>
            <TelegramShareButton url={URL}>
              <SocialIcon selected={selectedSocial === "Telegram"}>
                <BsTelegram />
              </SocialIcon>
            </TelegramShareButton>
            <SocialName>{t("members.applicationForm.Telegram")}</SocialName>
          </Social>
          <Social onClick={() => setSelectedSocial("WhatsApp")}>
            <WhatsappShareButton url={URL}>
              <SocialIcon selected={selectedSocial === "WhatsApp"}>
                <BsWhatsapp />
              </SocialIcon>
            </WhatsappShareButton>
            <SocialName>{t("members.applicationForm.WhatsApp")}</SocialName>
          </Social>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Subtitle>{t("members.applicationForm.CopyLink")}</Subtitle>
        <CopyContainer>
          <URLText>{URL}</URLText>
          <Button onClick={() => copyLink(URL)}>
            {copiedLink.value
              ? t("members.applicationForm.Copied")
              : t("members.applicationForm.Copy")}
          </Button>
        </CopyContainer>
      </Stack>
    </Stack>
  );
};
